/**
 * Connection Speed Measurement Service
 * Measures download speed and categorizes connection quality
 */

class ConnectionSpeedService {
  constructor() {
    this.speed = null;
    this.quality = 'medium';
    this.isRunning = false;
    this.lastMeasurement = null;
    this.measurementInterval = 30000; // 30 seconds
  }

  /**
   * Measure connection speed by downloading a test image
   * @returns {Promise<{speed: number, quality: string}>}
   */
  async measureSpeed() {
    if (this.isRunning) {
      return { speed: this.speed, quality: this.quality };
    }

    this.isRunning = true;

    try {
      // Use a small test image from a reliable CDN
      const testUrl = `https://via.placeholder.com/100x100/000000/FFFFFF.png?t=${Date.now()}`;
      const startTime = performance.now();

      const response = await fetch(testUrl, {
        cache: 'no-cache',
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const endTime = performance.now();

      const downloadTime = (endTime - startTime) / 1000; // Convert to seconds
      const fileSizeKB = blob.size / 1024;
      const speedKbps = fileSizeKB / downloadTime;

      this.speed = speedKbps;
      this.quality = this.categorizeSpeed(speedKbps);
      this.lastMeasurement = Date.now();

      return { speed: speedKbps, quality: this.quality };
    } catch (error) {
      console.warn('Connection speed measurement failed:', error);
      // Fallback to medium quality on error
      this.speed = 500;
      this.quality = 'medium';
      return { speed: this.speed, quality: this.quality };
    } finally {
      this.isRunning = false;
    }
  }

  /**
   * Categorize connection speed into quality levels
   * @param {number} speedKbps - Speed in kilobits per second
   * @returns {string} Quality level: 'slow', 'medium', 'fast'
   */
  categorizeSpeed(speedKbps) {
    if (speedKbps < 200) return 'slow';
    if (speedKbps < 1000) return 'medium';
    return 'fast';
  }

  /**
   * Get cached speed measurement or perform new measurement if needed
   * @returns {Promise<{speed: number, quality: string}>}
   */
  async getSpeed() {
    const now = Date.now();

    // Return cached measurement if recent enough
    if (this.lastMeasurement && (now - this.lastMeasurement) < this.measurementInterval) {
      return { speed: this.speed, quality: this.quality };
    }

    return await this.measureSpeed();
  }

  /**
   * Get thumbnail resolution based on connection quality
   * @param {string} quality - Connection quality level
   * @returns {number} Thumbnail resolution in pixels
   */
  getThumbnailResolution(quality = this.quality) {
    switch (quality) {
      case 'slow': return 80;    // Reduced from 180
      case 'medium': return 180;  // Current default
      case 'fast': return 200;    // Increased for fast connections
      default: return 180;
    }
  }

  /**
   * Check if connection uses Network Information API
   * @returns {boolean}
   */
  supportsNetworkInfo() {
    return 'connection' in navigator;
  }

  /**
   * Get network information from browser API if available
   * @returns {Object|null}
   */
  getNetworkInfo() {
    if (!this.supportsNetworkInfo()) return null;

    const connection = navigator.connection;
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt
    };
  }

  /**
   * Get quality estimate from Network Information API
   * @returns {string} Quality level based on effectiveType
   */
  getQualityFromNetworkInfo() {
    if (!this.supportsNetworkInfo()) return 'medium';

    const effectiveType = navigator.connection.effectiveType;

    switch (effectiveType) {
      case 'slow-2g':
      case '2g': return 'slow';
      case '3g': return 'medium';
      case '4g': return 'fast';
      default: return 'medium';
    }
  }

  /**
   * Get best available connection quality estimate
   * @returns {Promise<string>}
   */
  async getConnectionQuality() {
    // Try Network Information API first (faster)
    if (this.supportsNetworkInfo()) {
      const networkQuality = this.getQualityFromNetworkInfo();
      if (networkQuality !== 'medium') {
        this.quality = networkQuality;
        return networkQuality;
      }
    }

    // Fall back to speed test
    const { quality } = await this.getSpeed();
    return quality;
  }
}

// Export singleton instance
export const connectionSpeedService = new ConnectionSpeedService();
export default connectionSpeedService;